import { createContext, PropsWithChildren, useCallback, useMemo } from "react";

import { formatIntCurrency } from "~/helpers/numberHelpers";
import { Currency } from "~/entities/currencyEntities";
import { Tax } from "~/entities/taxEntities";
import { createLookupMapFor } from "~/helpers/objHelpers";

interface TaxContextProps {
  currency: Currency | null;
  taxes: Tax[];
}
interface TaxContextValue extends TaxContextProps {
  formatAmount: (
    amount: number | string,
    alwaysShowSign?: boolean,
  ) => string | null | undefined;
  taxFromCode: Record<string, Tax>;
}

const TaxContext = createContext<TaxContextValue>({
  currency: null,
  taxFromCode: {},
  taxes: [],
  formatAmount: () => null,
});

export const TaxContextProvider = ({
  children,
  currency,
  taxes,
}: PropsWithChildren<TaxContextProps>) => {
  const formatAmount = useCallback(
    (amount: number | string, alwaysShowSign?: boolean) =>
      !currency
        ? ""
        : `${alwaysShowSign && amount && +amount >= 0 ? "+" : ""}${formatIntCurrency(amount, currency)}`,
    [currency],
  );
  const taxFromCode = useMemo(
    () => createLookupMapFor(taxes, (x) => x.code),
    [taxes],
  );

  return (
    <TaxContext.Provider value={{ currency, taxes, taxFromCode, formatAmount }}>
      {children}
    </TaxContext.Provider>
  );
};

export default TaxContext;
